import { AfterViewInit, ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { DialogSidebarComponent } from '@features/dialog/dialog-sidebar/dialog-sidebar.component';
import { TranslateSafePipe } from '@shared/translate-safe/translate-safe.pipe';
import { TranslateModule } from '@ngx-translate/core';
import { AsyncPipe } from '@angular/common';
import { CheckboxComponent } from '@shared/form-elements/checkbox/checkbox.component';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { HandleInvalidOnSubmitDirective } from '@shared/form-elements/form-helpers/handle-invalid-on-submit.directive';
import { InputComponent } from '@shared/form-elements/input/input/input.component';
import { InputEmailComponent } from '@shared/form-elements/input/input-email/input-email.component';
import { InterceptLinksDirective } from '@shared/intercept-links/intercept-links.directive';
import { LoadingDotsComponent } from '@shared/loading-dots/loading-dots.component';
import { SafePipe } from '@shared/safe/safe.pipe';
import { SettingsService } from '@core/services/settings.service';
import { ActionStatus } from '@models/common.models';
import { NotifyMeDialogData, NotifyMeForm } from '@shared/notify-me/notify-me.model';
import { CustomValidatorsService } from '@shared/form-elements/custom-validators/custom-validators.service';
import { IMPACT_OVERLAY_DATA, ImpactOverlayRef } from '@libs/overlay';
import { ProductImageComponent } from '@shared/image/product-image/product-image.component';
import { NotifyMeService } from '@shared/notify-me/notify-me.service';
import { NotificationService } from '@core/services/notification/notification.service';

@Component({
    selector: 'app-notify-me-dialog',
    standalone: true,
    imports: [
        DialogSidebarComponent,
        TranslateSafePipe,
        TranslateModule,
        AsyncPipe,
        CheckboxComponent,
        ReactiveFormsModule,
        HandleInvalidOnSubmitDirective,
        InputComponent,
        InputEmailComponent,
        InterceptLinksDirective,
        LoadingDotsComponent,
        SafePipe,
        ProductImageComponent
    ],
    templateUrl: './notify-me-dialog.component.html',
    styleUrl: './notify-me-dialog.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class NotifyMeDialogComponent implements AfterViewInit {
    private readonly formBuilder = inject(FormBuilder);
    private readonly overlayRef = inject(ImpactOverlayRef);
    private readonly notifyMeService = inject(NotifyMeService);
    private readonly notificationService = inject(NotificationService);
    private readonly customValidatorsService = inject(CustomValidatorsService);
    public readonly data: NotifyMeDialogData = inject(IMPACT_OVERLAY_DATA, { optional: true });

    public settingsService = inject(SettingsService);

    public form: FormGroup<NotifyMeForm> = this.formBuilder.group<NotifyMeForm>({
        customerName: new FormControl<string>('', [Validators.required, Validators.maxLength(250)]),
        customerEmail: new FormControl<string>('', [
            Validators.required,
            Validators.maxLength(250),
            this.customValidatorsService.email
        ]),
        customerPhoneNumber: new FormControl<string>(''),
        customerConsent: new FormControl<boolean>(false, Validators.requiredTrue)
    });

    state = signal<ActionStatus>('idle');

    ngAfterViewInit(): void {
        this.form.updateValueAndValidity();
    }

    onSubmit(): void {
        if (this.form.invalid) {
            return;
        }

        if (!this.data?.product?.productNo) {
            return;
        }

        this.state.set('loading');
        this.notifyMeService
            .notifyMe({
                customerName: this.form.value.customerName ?? '',
                customerEmail: this.form.value.customerEmail ?? '',
                customerPhoneNumber: '',
                productEAN: this.data.product.productNo
            })
            .subscribe({
                next: () => {
                    this.state.set('success');
                    this.notificationService.createNotification({
                        type: 'success',
                        fadeTime: 5000,
                        translationHeadline: 'notifyMe.headings.notification',
                        translationText: 'notifyMe.feedback.success'
                    });
                    this.overlayRef.close();
                },
                error: () => {
                    this.state.set('error');
                    this.notificationService.createNotification({
                        type: 'error',
                        fadeTime: 5000,
                        translationHeadline: 'notifyMe.headings.notification',
                        translationText: 'notifyMe.feedback.error'
                    });
                }
            });
    }

    onDoneOrCancel = (): void => {
        this.overlayRef.close();
    };
}
